import type { TourAnchor } from './anchors'
import { RoleEnum, TrainerCapability } from '@zeus/index'
import { can } from '~/helpers/auth/can'
import { STAFF_ROLES } from '~/helpers/auth/staff-roles'

export type TourStep = {
  anchor: TourAnchor
  titleKey: string
  bodyKey: string
}

/**
 * Une visite guidée d'un écran (#78). `roles` reprend les rôles que la route
 * accepte (`router.tsx`, `back-office-nav.ts`) : proposer à un formateur la
 * visite d'un écran que son loader lui refuse le renverrait à l'accueil.
 * `capability` reprend de même la capacité exigée par le loader (#11).
 */
export type Tour = {
  id: string
  route: string
  roles: RoleEnum[]
  capability?: TrainerCapability
  /** Écran d'achat de formule : masqué tant que le libre-service est coupé (#165). */
  selfServiceBilling?: boolean
  titleKey: string
  descriptionKey: string
  steps: TourStep[]
}

/** Le registre des visites, versionné avec le code ; textes en i18n `fr` et `en`. */
const { Admin, Formateur } = RoleEnum
const STAFF = [...STAFF_ROLES]
const ACCOUNT_STEPS: TourAnchor[] = ['account.profile', 'account.identity', 'account.password', 'account.preferences']

/** Une étape : ses textes vivent sous `tours.<visite>.<élément>.title|body`. */
function step(tourId: string, anchor: TourAnchor): TourStep {
  const key = `tours.${tourId}.${anchor.split('.')[1]}`
  return { anchor, titleKey: `${key}.title`, bodyKey: `${key}.body` }
}

/** `textsId` : pour deux visites d'un même écran monté dans les deux cadres, un seul jeu de textes. */
function tour(id: string, route: string, roles: RoleEnum[], anchors: TourAnchor[], textsId = id): Tour {
  return {
    id,
    route,
    roles,
    titleKey: `tours.${textsId}.title`,
    descriptionKey: `tours.${textsId}.description`,
    steps: anchors.map(anchor => step(textsId, anchor)),
  }
}

export const TOURS: Tour[] = [
  // Une étape dont l'élément est masqué au porteur (droit retiré, #11) est
  // sautée par `TourLauncher` ; une visite part, elle, de sa PREMIÈRE ancre :
  // sans les statistiques, le tableau de bord n'a plus de quoi la porter.
  { ...tour('dashboard', '/back-office/dashboard', STAFF, ['dashboard.summary', 'dashboard.report', 'dashboard.tiles', 'dashboard.trend', 'dashboard.range', 'dashboard.alerts', 'dashboard.table', 'backoffice.help']), capability: TrainerCapability.STATISTICS_VIEW },
  { ...tour('modules', '/back-office/modules', STAFF, ['modules.storage', 'modules.import', 'modules.dropzone', 'modules.search', 'modules.filters', 'modules.list', 'modules.actions']), capability: TrainerCapability.MODULES_VIEW },
  { ...tour('sessions', '/back-office/sessions', STAFF, ['sessions.search', 'sessions.filters', 'sessions.new', 'sessions.list', 'sessions.trainers', 'sessions.status', 'sessions.actions']), capability: TrainerCapability.SESSIONS_VIEW },
  { ...tour('users', '/back-office/users', STAFF, ['users.search', 'users.filters', 'users.add', 'users.export', 'users.table', 'users.select', 'users.actions']), capability: TrainerCapability.LEARNERS_VIEW },
  // La fiche apprenant est à id : visitée sur sa démonstration (#242).
  { ...tour('learnerProfile', '/back-office/help/demo/learner', STAFF, ['profile.header', 'profile.indicators', 'profile.info', 'profile.groups', 'profile.sessions', 'profile.attendance', 'profile.certificates', 'profile.positioning', 'profile.logins']), capability: TrainerCapability.LEARNERS_VIEW },
  { ...tour('preview', '/help/demo/preview', STAFF, ['player.exit', 'player.header', 'player.chapters', 'player.content']), capability: TrainerCapability.MODULES_VIEW },
  // Écrans réservés à l'admin, comme leurs routes (`adminOnly` dans `back-office-nav.ts`).
  tour('groups', '/back-office/groups', [Admin], ['groups.search', 'groups.new', 'groups.list', 'groups.actions']),
  // La fiche d'une session porte un id : sa visite passe par sa démonstration (#243).
  { ...tour('sessionDetail', '/back-office/help/demo/session', STAFF, ['sessionDetail.header', 'sessionDetail.edit', 'sessionDetail.kpis', 'sessionDetail.survey', 'sessionDetail.info', 'sessionDetail.trainers', 'sessionDetail.path', 'sessionDetail.participants', 'sessionDetail.filter', 'sessionDetail.exports', 'sessionDetail.attendance', 'sessionDetail.dropout']), capability: TrainerCapability.SESSIONS_VIEW },
  { ...tour('questions', '/back-office/questions', STAFF, ['questions.filters', 'questions.table', 'questions.exports', 'questions.learners']), capability: TrainerCapability.STATISTICS_VIEW },
  // Le formateur n'a que la liste : les étapes de l'éditeur, absentes pour lui, sont sautées.
  { ...tour('reports', '/back-office/reports', STAFF, ['reports.list', 'reports.download', 'reports.editor', 'reports.granularity', 'reports.columns', 'reports.filters', 'reports.save', 'reports.schedule']), capability: TrainerCapability.STATISTICS_EXPORT },
  tour('certificates', '/back-office/certificates', [Admin], ['certificates.templates', 'certificates.template', 'certificates.variables', 'certificates.logo', 'certificates.background', 'certificates.email', 'certificates.preview', 'certificates.save']),
  tour('branding', '/back-office/settings', [Admin], ['branding.identity', 'branding.colors', 'branding.misc', 'branding.save']),
  tour('trainerCapabilities', '/back-office/trainer-capabilities', [Admin], ['trainerCapabilities.intro', 'trainerCapabilities.domains', 'trainerCapabilities.save']),
  tour('accommodations', '/back-office/accommodations', [Admin], ['accommodations.intro', 'accommodations.referent', 'accommodations.list']),
  tour('complaints', '/back-office/complaints', [Admin], ['complaints.intro', 'complaints.filters', 'complaints.export', 'complaints.new', 'complaints.list']),
  tour('documents', '/back-office/documents', [Admin], ['documents.intro', 'documents.new', 'documents.list']),
  tour('quality', '/back-office/quality', [Admin], ['quality.intro', 'quality.tabs', 'quality.export', 'quality.new', 'quality.list']),
  tour('surveyEditor', '/back-office/survey', [Admin], ['surveyEditor.intro', 'surveyEditor.questions', 'surveyEditor.add', 'surveyEditor.save']),
  tour('reminders', '/back-office/reminders', [Admin], ['reminders.cadence', 'reminders.message', 'reminders.digest', 'reminders.convocation', 'reminders.test', 'reminders.save']),
  tour('sso', '/back-office/sso', [Admin], ['sso.intro', 'sso.master', 'sso.picker', 'sso.provider', 'sso.save']),
  // Même route que la facturation plateforme, autre écran : l'admin voit SA facture.
  { ...tour('billing', '/back-office/billing', [Admin], ['billing.month', 'billing.estimate', 'billing.plan', 'billing.term', 'billing.subscribe', 'billing.manage', 'billing.profile', 'billing.history']), selfServiceBilling: true },
  tour('billingPlatform', '/back-office/billing', [RoleEnum.SuperAdmin], ['billing.month', 'billing.counters', 'billing.storage']),
  // Écrans de l'opérateur plateforme, comme leurs routes.
  tour('organizations', '/back-office/organizations', [RoleEnum.SuperAdmin], ['organizations.create', 'organizations.list', 'organizations.actions']),
  tour('trialRequests', '/back-office/trial-requests', [RoleEnum.SuperAdmin], ['trialRequests.intro', 'trialRequests.list', 'trialRequests.actions']),
  tour('webhooks', '/back-office/webhooks', [Admin], ['webhooks.intro', 'webhooks.endpoint', 'webhooks.save', 'webhooks.secret', 'webhooks.deliveries', 'webhooks.test']),
  // Côté apprenant. Les écrans à id (parcours, lecteur) sont visités sur leur
  // démonstration, à adresse fixe et sur données fictives (#105).
  tour('home', '/my-sessions', [RoleEnum.User], ['learner.dashboard', 'learner.search', 'learner.hero', 'learner.paths', 'learner.courses', 'learner.menu']),
  tour('path', '/help/demo/path', [RoleEnum.User], ['path.header', 'path.sheet', 'path.documents', 'path.positioning', 'path.steps', 'path.focus']),
  tour('player', '/help/demo/player', [RoleEnum.User], ['player.exit', 'player.header', 'player.chapters', 'player.content']),
  // « Mon compte » existe dans les deux cadres : même écran, même visite, deux routes.
  tour('account', '/back-office/account', [Admin, Formateur, RoleEnum.SuperAdmin], ACCOUNT_STEPS),
  tour('account-learner', '/account', [RoleEnum.User], [...ACCOUNT_STEPS, 'account.accommodation'], 'account'),
]

/** Les visites proposées à un rôle et à ses capacités, dans l'ordre du registre. */
export function toursForRole(
  role: RoleEnum | string | undefined,
  tours: Tour[] = TOURS,
  capabilities: readonly TrainerCapability[] | null = null,
  { billingSelfService = false }: { billingSelfService?: boolean | null } = {},
): Tour[] {
  if (!role) return []
  return tours.filter(tour => tour.roles.includes(role as RoleEnum)
    && (!tour.capability || can(role, capabilities, tour.capability))
    && (!tour.selfServiceBilling || billingSelfService))
}
